// Flow 5 tail + Flow 6 entry - `AppDetail`'s "Build" sub-nav tab (docs/phase-0/
// slice5-ux-flows.md §2.4, §8.7). Content by `app.stage`: `mvp` -> the calm "Refine the
// MVP" status + "Start Production Build" (owner/admin-only, viewer sees it disabled);
// `production` -> a calm read-only summary with no primary action (one primary action
// per screen, `frontend-design`'s `primary-action` rule - a Production-stage app's
// Build tab has none); `validated` reached directly -> a link back into the guided
// setup rather than a dead end.
import { Link, useOutletContext } from "react-router-dom";
import type { AppDetailContext } from "../AppDetail.js";
import { StageBadge } from "../../components/StageBadge.js";
import { buildCopy } from "../../copy/build.js";
import { isOwnerOrAdmin, useSession } from "../../state/session.js";

export const AppBuildTab = (): React.JSX.Element => {
  const { appId, app } = useOutletContext<AppDetailContext>();
  const { state: session } = useSession();
  const canManage = isOwnerOrAdmin(session);

  return (
    <div>
      <div className="row aic gap12">
        <h1 className="h2">{buildCopy("build.tab.title")}</h1>
        <StageBadge stage={app.stage} />
      </div>

      {app.stage === "validated" ? (
        <div className="card pad mt16" data-testid="build-tab-validated">
          <p className="muted">Finish the guided setup to build {app.name}.</p>
          <Link className="btn btn-primary mt8" to={`/apps/${appId}/build/workflow`}>
            Continue setup
          </Link>
        </div>
      ) : null}

      {app.stage === "mvp" ? (
        <div className="card pad mt16" data-testid="build-tab-mvp">
          <h2 className="h3">{buildCopy("build.tab.mvp.heading")}</h2>
          <p className="muted mt8">{buildCopy("build.tab.mvp.body", { appName: app.name })}</p>
          {canManage ? (
            <Link className="btn btn-primary mt12" to={`/apps/${appId}/build/workflow`}>
              {buildCopy("build.tab.mvp.startProductionCta")}
            </Link>
          ) : (
            <span className="tooltip-wrap mt12">
              <button
                type="button"
                className="btn btn-primary"
                disabled
                aria-describedby="build-tab-viewer-reason"
              >
                {buildCopy("build.tab.mvp.startProductionCta")}
              </button>
              <span className="tooltip-bubble" id="build-tab-viewer-reason" role="tooltip">
                {buildCopy("build.tab.deniedRole")}
              </span>
            </span>
          )}
        </div>
      ) : null}

      {app.stage === "production" ? (
        <div className="card pad mt16" data-testid="build-tab-production">
          <h2 className="h3">{buildCopy("build.tab.production.heading")}</h2>
          <p className="muted mt8">{buildCopy("build.tab.production.body", { appName: app.name })}</p>
          <Link className="btn btn-ghost mt12" to={`/apps/${appId}/certify`}>
            {buildCopy("build.tab.production.certifyLinkCta")}
          </Link>
        </div>
      ) : null}
    </div>
  );
};
